import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Paper,
  Typography,
  Container,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { ChevronLeft, ChevronRight, CheckCircle, Pending, ArrowBack } from '@mui/icons-material';
import BasicDetailsForm from './forms/BasicDetailsForm';
import ThirdPartyRiskForm from './forms/ThirdPartyRiskForm';
// import ApprovalDueDiligenceForm from './forms/ApprovalDueDiligenceForm';
import CommercialDueDiligenceForm from './forms/CommercialDueDiligenceForm';
import BidsForm from './forms/BidsForm';
import SupplierReviewForm from './forms/SupplierReviewForm';
import RejectionBanner from './RejectionBanner';
import { useNotification } from './NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import {
  validateAllForms,
  hasValidationErrors,
  getValidationSummary,
  FormValidationErrors,
} from '../utils/formValidation';

type WorkflowStatus = 'DRAFT' | 'PENDING_MANAGER_APPROVAL' | 'PENDING_LEGAL' | 'PENDING_AP' | 'COMPLETED' | 'REJECTED';

interface FormData {
  workflow: {
    status: WorkflowStatus;
    submittedDate?: string;
    approverApprovalDate?: string;
    legalApprovalDate?: string;
    apApprovalDate?: string;
    requiresLegalReview: boolean;
    rejectionReason?: string;
  };
  basicDetails: {
    estimatedContractAmount: string;
    requestDate: string;
    requestorName: string;
    requestorCode?: string;
    requestorEmail?: string;
    requestorManager: string;
    requestorManagerCode?: string;
    requestorManagerEmail?: string;
    companyName: string;
    streetAddress: string;
    addressLine2: string;
    city: string;
    zipCode: string;
    state: string;
    country: string;
    phone: string;
    contactPerson: string;
    contactEmail: string;
    vendorWebsite: string;
    howLearnedAbout: string;
    goodsServicesDescription: string;
    competitiveBidsObtained: string;
  };
  thirdPartyRisk: {
    ofacSanctions: string;
    ofacSanctionsDetails: string;
    angelRelationships: string;
    angelRelationshipsDetails: string;
    personalInfoAccess: string;
    personalInfoDetails: string;
    specialCircumstances: string;
    specialCircumstancesDetails: string;
  };
  approvalDueDiligence: {
    approverName: string;
    legalClearance: string;
    notes: string;
  };
  commercialDueDiligence: {
    reputationalConcerns: string;
    reputationalConcernsDetails: string;
    researchDate: string;
    searchTermsTools: string;
    experienceCredentials: string;
    canProvideReferences: string;
    references: string;
    hasRequiredLicenses: string;
    licenseExplanation: string;
  };
  bids: {
    bid1Description: string;
    bid1Total: string;
    bid2Description: string;
    bid2Total: string;
    bid3Description: string;
    bid3Total: string;
    isLowestBid: string;
    selectionReason: string;
  };
}

const MultiStepForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({
    basicDetails: [],
    thirdPartyRisk: [],
    commercialDueDiligence: [],
    bids: [],
  });
  const [formData, setFormData] = useState<FormData>({
    workflow: {
      status: 'DRAFT',
      requiresLegalReview: false,
    },
    basicDetails: {
      estimatedContractAmount: '',
      requestDate: '',
      requestorName: '',
      requestorCode: '',
      requestorEmail: '',
      requestorManager: '',
      requestorManagerCode: '',
      requestorManagerEmail: '',
      companyName: '',
      streetAddress: '',
      addressLine2: '',
      city: '',
      zipCode: '',
      state: '',
      country: '',
      phone: '',
      contactPerson: '',
      contactEmail: '',
      vendorWebsite: '',
      howLearnedAbout: '',
      goodsServicesDescription: '',
      competitiveBidsObtained: '',
    },
    thirdPartyRisk: {
      ofacSanctions: '',
      ofacSanctionsDetails: '',
      angelRelationships: '',
      angelRelationshipsDetails: '',
      personalInfoAccess: '',
      personalInfoDetails: '',
      specialCircumstances: '',
      specialCircumstancesDetails: '',
    },
    approvalDueDiligence: {
      approverName: '',
      legalClearance: '',
      notes: '',
    },
    commercialDueDiligence: {
      reputationalConcerns: '',
      reputationalConcernsDetails: '',
      researchDate: '',
      searchTermsTools: '',
      experienceCredentials: '',
      canProvideReferences: '',
      references: '',
      hasRequiredLicenses: '',
      licenseExplanation: '',
    },
    bids: {
      bid1Description: '',
      bid1Total: '',
      bid2Description: '',
      bid2Total: '',
      bid3Description: '',
      bid3Total: '',
      isLowestBid: '',
      selectionReason: '',
    },
  });

  // Load existing data when ID is present
  useEffect(() => {
    const loadFormData = async () => {
      // For new forms, auto-select requestor from employee list based on logged-in user's email
      if (id === 'new') {
        if (user?.email) {
          try {
            // Dynamically import employee data to find matching employee
            const { loadEmployees } = await import('../utils/employeeData');
            const employees = await loadEmployees();
            const matchingEmployee = employees.find(emp => 
              emp.workEmail.toLowerCase() === user.email.toLowerCase()
            );
            
            if (matchingEmployee) {
              setFormData((prev) => ({
                ...prev,
                basicDetails: {
                  ...prev.basicDetails,
                  requestorName: matchingEmployee.employeeName,
                  requestorCode: matchingEmployee.employeeCode,
                  requestorEmail: matchingEmployee.workEmail,
                },
              }));
            }
          } catch (error) {
            console.error('Error loading employee data:', error);
          }
        }
        setLoading(false);
        return;
      }
      
      if (id && id !== 'new') {
        try {
          const response = await apiClient.get(`/api/vendors/${id}`);
          const dbData = response.data.data;
          
          // Map database fields to form structure
          setFormData({
              workflow: {
                status: dbData.workflow_status || 'DRAFT',
                submittedDate: dbData.submitted_date,
                approverApprovalDate: dbData.approver_approval_date,
                legalApprovalDate: dbData.legal_approval_date,
                apApprovalDate: dbData.ap_approval_date,
                requiresLegalReview: dbData.requires_legal_review || false,
                rejectionReason: dbData.rejection_reason,
              },
              basicDetails: {
                estimatedContractAmount: dbData.estimated_contract_amount || '',
                requestDate: dbData.request_date || '',
                requestorName: dbData.requestor_name || '',
                requestorCode: dbData.requestor_code || '',
                requestorEmail: dbData.requestor_email || '',
                requestorManager: dbData.requestor_manager || '',
                requestorManagerCode: dbData.requestor_manager_code || '',
                requestorManagerEmail: dbData.requestor_manager_email || '',
                companyName: dbData.company_name || '',
                streetAddress: dbData.street_address || '',
                addressLine2: dbData.address_line_2 || '',
                city: dbData.city || '',
                zipCode: dbData.zip_code || '',
                state: dbData.state || '',
                country: dbData.country || '',
                phone: dbData.phone || '',
                contactPerson: dbData.contact_person || '',
                contactEmail: dbData.contact_email || '',
                vendorWebsite: dbData.vendor_website || '',
                howLearnedAbout: dbData.how_learned_about || '',
                goodsServicesDescription: dbData.goods_services_description || '',
                competitiveBidsObtained: dbData.competitive_bids_obtained || '',
              },
              thirdPartyRisk: {
                ofacSanctions: dbData.ofac_sanctions || '',
                ofacSanctionsDetails: dbData.ofac_sanctions_details || '',
                angelRelationships: dbData.angel_relationships || '',
                angelRelationshipsDetails: dbData.angel_relationships_details || '',
                personalInfoAccess: dbData.personal_info_access || '',
                personalInfoDetails: dbData.personal_info_details || '',
                specialCircumstances: dbData.special_circumstances || '',
                specialCircumstancesDetails: dbData.special_circumstances_details || '',
              },
              approvalDueDiligence: {
                approverName: dbData.approver_name || '',
                legalClearance: dbData.legal_clearance || '',
                notes: dbData.approval_notes || '',
              },
              commercialDueDiligence: {
                reputationalConcerns: dbData.reputational_concerns || '',
                reputationalConcernsDetails: dbData.reputational_concerns_details || '',
                researchDate: dbData.research_date || '',
                searchTermsTools: dbData.search_terms_tools || '',
                experienceCredentials: dbData.experience_credentials || '',
                canProvideReferences: dbData.can_provide_references || '',
                references: dbData.supplier_references || '',
                hasRequiredLicenses: dbData.has_required_licenses || '',
                licenseExplanation: dbData.license_explanation || '',
              },
              bids: {
                bid1Description: dbData.bid_1_description || '',
                bid1Total: dbData.bid_1_total || '',
                bid2Description: dbData.bid_2_description || '',
                bid2Total: dbData.bid_2_total || '',
                bid3Description: dbData.bid_3_description || '',
                bid3Total: dbData.bid_3_total || '',
                isLowestBid: dbData.is_lowest_bid || '',
                selectionReason: dbData.selection_reason || '',
              },
          });
        } catch (error) {
          console.error('Error loading form data:', error);
        }
      }
      setLoading(false);
    };

    loadFormData();
  }, [id, user]);

  // Mock data loader (will be replaced with Snowflake query)
  /*const getMockFormData = (requestId: number): FormData | null => {
    const mockDataMap: { [key: number]: FormData } = {
      1: {
        workflow: {
          status: 'PENDING_MANAGER_APPROVAL',
          submittedDate: '2026-01-15T10:30:00',
          requiresLegalReview: false,
        },
        basicDetails: {
          estimatedContractAmount: '50000',
          requestDate: '2026-01-15',
          requestorName: 'ROSE, TYLER',
          requestorCode: 'A0JW',
          requestorManager: 'WILLARDSON, RAY',
          requestorManagerCode: 'A060',
          companyName: 'Acme Corporation',
          streetAddress: '123 Main St',
          addressLine2: 'Suite 100',
          zipCodeCity: 'Los Angeles, CA 90001',
          country: 'United States',
          phone: '555-1234',
          contactPerson: 'Jane Acme',
          contactEmail: 'jane@acme.com',
          howLearnedAbout: 'Industry referral',
          goodsServicesDescription: 'Marketing services for film production',
          competitiveBidsObtained: 'Yes',
        },
        thirdPartyRisk: {
          angelRelationships: 'No',
          angelRelationshipsDetails: '',
          personalInfoAccess: 'No',
          personalInfoDetails: '',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: 'Sarah Johnson',
          legalClearance: 'Yes',
          notes: 'Standard approval process',
        },
        commercialDueDiligence: {
          reputationalConcerns: 'No',
          reputationalConcernsDetails: '',
          researchDate: '2026-01-14',
          searchTermsTools: 'Google, LinkedIn',
          experienceCredentials: '10 years in marketing',
          canProvideReferences: 'Yes',
          references: 'ABC Studios, XYZ Films',
          hasRequiredLicenses: 'Yes',
          licenseExplanation: 'Licensed marketing agency',
        },
        bids: {
          bid1Description: 'Acme Corporation - Full service package',
          bid1Total: '50000',
          bid2Description: 'Competitor A - Basic package',
          bid2Total: '45000',
          bid3Description: 'Competitor B - Premium package',
          bid3Total: '60000',
          selectionReason: 'Best value for comprehensive services',
        },
      },
      2: {
        workflow: {
          status: 'COMPLETED',
          submittedDate: '2026-01-10T09:00:00',
          approverApprovalDate: '2026-01-11T10:30:00',
          legalApprovalDate: '2026-01-12T14:00:00',
          apApprovalDate: '2026-01-13T16:00:00',
          requiresLegalReview: false,
        },
        basicDetails: {
          estimatedContractAmount: '75000',
          requestDate: '2026-01-10',
          requestorName: 'DOE, JANE',
          requestorCode: 'A0HZ',
          requestorManager: 'FISCHER, DAVID',
          requestorManagerCode: 'A072',
          companyName: 'Tech Solutions Inc',
          streetAddress: '789 Tech Blvd',
          addressLine2: '',
          zipCodeCity: 'Seattle, WA 98101',
          country: 'United States',
          phone: '555-5678',
          contactPerson: 'Mike Tech',
          contactEmail: 'mike@techsolutions.com',
          howLearnedAbout: 'Industry conference',
          goodsServicesDescription: 'Software development services',
          competitiveBidsObtained: 'Yes',
        },
        thirdPartyRisk: {
          angelRelationships: 'No',
          angelRelationshipsDetails: '',
          personalInfoAccess: 'Yes',
          personalInfoDetails: 'Will have access to user data for analytics',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: 'David Fischer',
          legalClearance: 'Yes',
          notes: 'All checks completed successfully',
        },
        commercialDueDiligence: {
          reputationalConcerns: 'No',
          reputationalConcernsDetails: '',
          researchDate: '2026-01-09',
          searchTermsTools: 'Google, Glassdoor, LinkedIn',
          experienceCredentials: '8 years in software development',
          canProvideReferences: 'Yes',
          references: 'StartupXYZ, BigCorp Inc',
          hasRequiredLicenses: 'Yes',
          licenseExplanation: 'Business license and certifications',
        },
        bids: {
          bid1Description: 'Tech Solutions Inc - Full stack development',
          bid1Total: '75000',
          bid2Description: 'DevShop A - Frontend only',
          bid2Total: '50000',
          bid3Description: 'CodeWorks - Full package',
          bid3Total: '85000',
          selectionReason: 'Best technical expertise and reasonable pricing',
        },
      },
      3: {
        workflow: {
          status: 'DRAFT',
          requiresLegalReview: false,
        },
        basicDetails: {
          estimatedContractAmount: '25000',
          requestDate: '2026-01-20',
          requestorName: 'JOHNSON, BOB',
          requestorCode: 'A05N',
          requestorManager: 'REILLY, PATRICK',
          requestorManagerCode: 'A064',
          companyName: 'Global Services LLC',
          streetAddress: '321 Service Road',
          addressLine2: 'Building C',
          zipCodeCity: 'Austin, TX 78701',
          country: 'United States',
          phone: '555-9012',
          contactPerson: 'Sally Services',
          contactEmail: 'sally@globalservices.com',
          howLearnedAbout: 'Web search',
          goodsServicesDescription: 'Consulting services',
          competitiveBidsObtained: 'No',
        },
        thirdPartyRisk: {
          angelRelationships: 'No',
          angelRelationshipsDetails: '',
          personalInfoAccess: 'No',
          personalInfoDetails: '',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: '',
          legalClearance: '',
          notes: '',
        },
        commercialDueDiligence: {
          reputationalConcerns: '',
          reputationalConcernsDetails: '',
          researchDate: '',
          searchTermsTools: '',
          experienceCredentials: '',
          canProvideReferences: '',
          references: '',
          hasRequiredLicenses: '',
          licenseExplanation: '',
        },
        bids: {
          bid1Description: '',
          bid1Total: '',
          bid2Description: '',
          bid2Total: '',
          bid3Description: '',
          bid3Total: '',
          selectionReason: 'Still comparing options',
        },
      },
      4: {
        workflow: {
          status: 'PENDING_LEGAL',
          submittedDate: '2026-01-18T14:20:00',
          approverApprovalDate: '2026-01-19T09:15:00',
          requiresLegalReview: true,
        },
        basicDetails: {
          estimatedContractAmount: '100000',
          requestDate: '2026-01-18',
          requestorName: 'NEISH, JEREMY J',
          requestorCode: 'A056',
          requestorManager: 'NAHRWOLD, PAUL',
          requestorManagerCode: 'A0CO',
          companyName: 'Innovative Designs',
          streetAddress: '456 Design Ave',
          addressLine2: '',
          zipCodeCity: 'San Francisco, CA 94102',
          country: 'United States',
          phone: '555-9876',
          contactPerson: 'Tom Designer',
          contactEmail: 'tom@innovativedesigns.com',
          howLearnedAbout: 'Previous collaboration',
          goodsServicesDescription: 'Set design and construction services',
          competitiveBidsObtained: 'Yes',
        },
        thirdPartyRisk: {
          angelRelationships: 'Yes',
          angelRelationshipsDetails: 'Owner is cousin of board member',
          personalInfoAccess: 'No',
          personalInfoDetails: '',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: 'Michael Chen',
          legalClearance: 'Pending',
          notes: 'Needs legal review due to relationship',
        },
        commercialDueDiligence: {
          reputationalConcerns: 'No',
          reputationalConcernsDetails: '',
          researchDate: '2026-01-17',
          searchTermsTools: 'Google, Industry databases',
          experienceCredentials: '15 years in set design',
          canProvideReferences: 'Yes',
          references: 'Major Studios Inc, Film Productions LLC',
          hasRequiredLicenses: 'Yes',
          licenseExplanation: 'State contractor license',
        },
        bids: {
          bid1Description: 'Innovative Designs - Complete package',
          bid1Total: '100000',
          bid2Description: 'Design Studio A',
          bid2Total: '95000',
          bid3Description: 'Build Co',
          bid3Total: '110000',
          selectionReason: 'Best design quality and timeline',
        },
      },
    };
    return mockDataMap[requestId] || null;
  };*/

  const steps = [
    'Basic Details',
    'Third Party Risk',
    'Commercial Due Diligence',
    'Bids',
    'Review & Submit',
  ];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormData = <T extends keyof FormData>(section: T, data: FormData[T]) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  // Check if Legal review is required based on Third Party Risk answers
  // const checkLegalReviewRequired = () => {
  //   const { angelRelationships, personalInfoAccess, specialCircumstances } = formData.thirdPartyRisk;
  //   // Legal review only required if ANY answer is Yes (skip if ALL are No)
  //   return angelRelationships === 'YES' || personalInfoAccess === 'YES' || specialCircumstances === 'YES';
  // };

  // Map form data to API payload format
  const mapFormDataToAPI = (data: FormData) => {
    return {
      estimated_contract_amount: data.basicDetails.estimatedContractAmount,
      request_date: data.basicDetails.requestDate,
      requestor_name: data.basicDetails.requestorName,
      requestor_code: data.basicDetails.requestorCode,
      requestor_email: data.basicDetails.requestorEmail,
      requestor_manager: data.basicDetails.requestorManager,
      requestor_manager_code: data.basicDetails.requestorManagerCode,
      requestor_manager_email: data.basicDetails.requestorManagerEmail,
      company_name: data.basicDetails.companyName,
      street_address: data.basicDetails.streetAddress,
      address_line_2: data.basicDetails.addressLine2,
      city: data.basicDetails.city,
      zip_code: data.basicDetails.zipCode,
      state: data.basicDetails.state,
      country: data.basicDetails.country,
      phone: data.basicDetails.phone,
      contact_person: data.basicDetails.contactPerson,
      contact_email: data.basicDetails.contactEmail,
      vendor_website: data.basicDetails.vendorWebsite,
      how_learned_about: data.basicDetails.howLearnedAbout,
      goods_services_description: data.basicDetails.goodsServicesDescription,
      competitive_bids_obtained: data.basicDetails.competitiveBidsObtained,
      ofac_sanctions: data.thirdPartyRisk.ofacSanctions,
      ofac_sanctions_details: data.thirdPartyRisk.ofacSanctionsDetails,
      angel_relationships: data.thirdPartyRisk.angelRelationships,
      angel_relationships_details: data.thirdPartyRisk.angelRelationshipsDetails,
      personal_info_access: data.thirdPartyRisk.personalInfoAccess,
      personal_info_details: data.thirdPartyRisk.personalInfoDetails,
      special_circumstances: data.thirdPartyRisk.specialCircumstances,
      special_circumstances_details: data.thirdPartyRisk.specialCircumstancesDetails,
      approver_name: data.approvalDueDiligence.approverName,
      legal_clearance: data.approvalDueDiligence.legalClearance,
      approval_notes: data.approvalDueDiligence.notes,
      reputational_concerns: data.commercialDueDiligence.reputationalConcerns,
      reputational_concerns_details: data.commercialDueDiligence.reputationalConcernsDetails,
      research_date: data.commercialDueDiligence.researchDate,
      search_terms_tools: data.commercialDueDiligence.searchTermsTools,
      experience_credentials: data.commercialDueDiligence.experienceCredentials,
      can_provide_references: data.commercialDueDiligence.canProvideReferences,
      supplier_references: data.commercialDueDiligence.references,
      has_required_licenses: data.commercialDueDiligence.hasRequiredLicenses,
      license_explanation: data.commercialDueDiligence.licenseExplanation,
      bid_1_description: data.bids.bid1Description,
      bid_1_total: data.bids.bid1Total,
      bid_2_description: data.bids.bid2Description,
      bid_2_total: data.bids.bid2Total,
      bid_3_description: data.bids.bid3Description,
      bid_3_total: data.bids.bid3Total,
      is_lowest_bid: data.bids.isLowestBid,
      selection_reason: data.bids.selectionReason,
    };
  };

  // Save form data (auto-save or manual save)
  const handleSaveForm = async (): Promise<string | boolean> => {
    try {
      const payload = mapFormDataToAPI(formData);
      
      if (id && id !== 'new') {
        // Update existing form
        await apiClient.put(`/api/vendors/${id}`, payload);
        showNotification('Draft saved successfully', 'success');
        
        // Navigate to dashboard after save
        setTimeout(() => navigate('/'), 1500);
        return id;
      } else {
        // Create new form
        const response = await apiClient.post('/api/vendors', payload);
        const newId = response.data.id;
        
        showNotification('Draft saved successfully', 'success');
        
        // Navigate to dashboard after save
        setTimeout(() => navigate('/'), 1500);
        return newId;
      }
    } catch (error) {
      console.error('Error saving form:', error);
      showNotification('Error saving form', 'error');
      return false;
    }
  };

  // Handle form submission by requestor
  const handleSubmitForApproval = async () => {
    // Run comprehensive validation
    const errors = validateAllForms(
      formData.basicDetails,
      formData.thirdPartyRisk,
      formData.commercialDueDiligence,
      formData.bids
    );
    
    setValidationErrors(errors);
    
    // Check if there are any validation errors
    if (hasValidationErrors(errors)) {
      const summary = getValidationSummary(errors);
      showNotification(`Please fix validation errors: ${summary}`, 'error');
      
      // Navigate to the first form with errors
      if (errors.basicDetails.length > 0) {
        setActiveStep(0);
      } else if (errors.thirdPartyRisk.length > 0) {
        setActiveStep(1);
      } else if (errors.commercialDueDiligence.length > 0) {
        setActiveStep(2);
      } else if (errors.bids.length > 0) {
        setActiveStep(3);
      }
      
      return;
    }
    
    // Clear validation errors if all passed
    setValidationErrors({
      basicDetails: [],
      thirdPartyRisk: [],
      commercialDueDiligence: [],
      bids: [],
    });
    
    // Save first to get an ID if it's a new form
    const savedId = await handleSaveForm();
    if (!savedId) {
      showNotification('Failed to save form', 'error');
      return;
    }
    
    const formId = savedId === true ? id : savedId;
    
    try {
      await apiClient.post(`/api/vendors/${formId}/submit`);
      showNotification('Form submitted for approval', 'success');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error('Error submitting form:', error);
      showNotification('Error submitting form', 'error');
    }
  };

  // Handle manager approval
  const handleManagerApproval = async () => {
    try {
      const response = await apiClient.post(`/api/vendors/${id}/approve`, { stage: 'manager' });
      const result = response.data;
        setFormData((prev) => ({
          ...prev,
          workflow: {
            ...prev.workflow,
            status: result.newStatus,
            approverApprovalDate: new Date().toISOString(),
          },
        }));
        const requiresLegal = formData.workflow.requiresLegalReview;
        showNotification(
          `Approved by manager. ${requiresLegal ? 'Routing to Legal.' : 'Routing to AP.'}`,
          'success'
        );
    } catch (error) {
      console.error('Error approving:', error);
      showNotification('Error approving submission', 'error');
    }
  };

  // Handle legal approval
  const handleLegalApproval = async () => {
    try {
      await apiClient.post(`/api/vendors/${id}/approve`, { stage: 'legal' });
        setFormData((prev) => ({
          ...prev,
          workflow: {
            ...prev.workflow,
            status: 'PENDING_AP',
            legalApprovalDate: new Date().toISOString(),
          },
        }));
        showNotification('Approved by Legal. Routing to AP.', 'success');
    } catch (error) {
      console.error('Error approving:', error);
      showNotification('Error approving submission', 'error');
    }
  };

  // Handle AP completion
  const handleAPCompletion = async () => {
    try {
      await apiClient.post(`/api/vendors/${id}/approve`, { stage: 'ap' });
        setFormData((prev) => ({
          ...prev,
          workflow: {
            ...prev.workflow,
            status: 'COMPLETED',
            apApprovalDate: new Date().toISOString(),
          },
        }));
        showNotification('Vendor created in NetSuite. Process complete!', 'success');
    } catch (error) {
      console.error('Error completing:', error);
      showNotification('Error completing submission', 'error');
    }
  };

  // Handle rejection
  const handleRejection = () => {
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    // Validate rejection reason is not empty
    if (!rejectionNotes || rejectionNotes.trim().length === 0) {
      showNotification('Rejection reason is required. Please provide a detailed explanation.', 'error');
      return;
    }
    
    // Ensure minimum length for meaningful rejection reason
    if (rejectionNotes.trim().length < 10) {
      showNotification('Please provide a more detailed rejection reason (at least 10 characters).', 'error');
      return;
    }
    
    try {
      await apiClient.post(`/api/vendors/${id}/reject`, { reason: rejectionNotes });
      setFormData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          status: 'REJECTED',
          rejectionReason: rejectionNotes,
        },
      }));
      showNotification('Form rejected', 'success');
      setRejectDialogOpen(false);
      setRejectionNotes('');
    } catch (error) {
      console.error('Error rejecting form:', error);
      showNotification('Error rejecting form', 'error');
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setRejectionNotes('');
  };

  // Get workflow status display
  const getWorkflowStatusChip = () => {
    const { status } = formData.workflow;
    const statusConfig: Record<WorkflowStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
      DRAFT: { label: 'Draft', color: 'default' },
      PENDING_MANAGER_APPROVAL: { label: 'Pending Manager Approval', color: 'warning' },
      PENDING_LEGAL: { label: 'Pending Legal Review', color: 'info' },
      PENDING_AP: { label: 'Pending AP Approval', color: 'primary' },
      COMPLETED: { label: 'Completed', color: 'success' },
      REJECTED: { label: 'Rejected', color: 'error' },
    };
    const config = statusConfig[status];
    return <Chip label={config.label} color={config.color} icon={status === 'COMPLETED' ? <CheckCircle /> : <Pending />} />;
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BasicDetailsForm 
            data={formData.basicDetails} 
            onChange={(data) => updateFormData('basicDetails', data)}
            disabled={formData.workflow.status !== 'DRAFT'}
            errors={validationErrors.basicDetails}
          />
        );
      case 1:
        return (
          <ThirdPartyRiskForm 
            data={formData.thirdPartyRisk} 
            onChange={(data) => updateFormData('thirdPartyRisk', data)}
            disabled={formData.workflow.status !== 'DRAFT'}
            errors={validationErrors.thirdPartyRisk}
          />
        );
      case 2:
        return (
          <CommercialDueDiligenceForm 
            data={formData.commercialDueDiligence} 
            onChange={(data) => updateFormData('commercialDueDiligence', data)}
            disabled={formData.workflow.status !== 'DRAFT'}
            estimatedContractAmount={formData.basicDetails.estimatedContractAmount}
            errors={validationErrors.commercialDueDiligence}
          />
        );
      case 3:
        return (
          <BidsForm 
            data={formData.bids} 
            onChange={(data) => updateFormData('bids', data)}
            disabled={formData.workflow.status !== 'DRAFT'}
            errors={validationErrors.bids}
          />
        );
      case 4:
        return <SupplierReviewForm formData={formData} />;
      default:
        return null;
    }
  };

  const progress = (activeStep / (steps.length - 1)) * 100;
  const nextStepName = activeStep < steps.length - 1 ? steps[activeStep + 1] : null;

  // Determine if form is in read-only mode (COMPLETE status disables all fields)
  // const isReadOnly = formData.workflow.status === 'COMPLETE';

  // Determine current user role (this would come from authentication in real implementation)
  // const getCurrentUserRole = (): 'requestor' | 'approver' | 'legal' | 'ap' => {
  //   // TODO: Get from authentication context
  //   return 'requestor'; // Default for now
  // };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="text.secondary">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Back to Dashboard Button */}
      <Box sx={{ mb: 1 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          variant="text"
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Important Notice */}
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>This form does NOT approve spending!</strong> It only approves adding the vendor to the financial system and must be completed before any spending can be approved.
      </Alert>

      {/* Rejection Banner - Sticky at top */}
      {formData.workflow.status === 'REJECTED' && formData.workflow.rejectionReason && (
        <RejectionBanner rejectionReason={formData.workflow.rejectionReason} />
      )}

      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4, md: 5 }, mt: 2, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: formData.basicDetails.companyName ? 1 : 4 }}>
          Supplier Due Diligence {id && id !== 'new' ? `#${id}` : ''}
        </Typography>
        
        {formData.basicDetails.companyName && (
          <Typography variant="h6" component="h2" sx={{ mb: 2, textAlign: 'left', color: 'text.secondary' }}>
            {formData.basicDetails.companyName}
          </Typography>
        )}
        
        {/* Workflow Status */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>{getWorkflowStatusChip()}</Box>
          {formData.workflow.requiresLegalReview && formData.workflow.status !== 'DRAFT' && (
            <Chip label="Legal Review Required" color="info" size="small" />
          )}
        </Box>

        {/* Workflow Info Messages */}
        {formData.workflow.status === 'PENDING_MANAGER_APPROVAL' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Awaiting approval from: <strong>{formData.basicDetails.requestorManager || 'Manager'}</strong>
          </Alert>
        )}
        {formData.workflow.status === 'PENDING_LEGAL' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Awaiting legal review from: <strong>Glen</strong>
          </Alert>
        )}
        {formData.workflow.status === 'PENDING_AP' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Awaiting final approval from AP: <strong>Melody & Victoria</strong>
          </Alert>
        )}
        {formData.workflow.status === 'COMPLETED' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <strong>Process Complete!</strong> Vendor has been created in NetSuite.
          </Alert>
        )}
        {formData.workflow.status === 'REJECTED' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>Form Rejected:</strong> {formData.workflow.rejectionReason || 'No reason provided'}
          </Alert>
        )}
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" color="primary">
              {steps[activeStep]}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }} 
          />
        </Box>

        {/* Top Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ChevronLeft />}
            size="large"
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {(formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED') && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleSaveForm}
              >
                Save Draft
              </Button>
            )}
            {activeStep === steps.length - 1 && (formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED') ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmitForApproval}
              >
                Submit for Approval
              </Button>
            ) : activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ChevronRight />}
                size="large"
              >
                Next
              </Button>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Approval Action Buttons */}
        {formData.workflow.status === 'PENDING_MANAGER_APPROVAL' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleManagerApproval}
            >
              Approve (Manager)
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={handleRejection}
            >
              Reject
            </Button>
          </Box>
        )}
        {formData.workflow.status === 'PENDING_LEGAL' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleLegalApproval}
            >
              Approve (Legal)
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={handleRejection}
            >
              Reject
            </Button>
          </Box>
        )}
        {formData.workflow.status === 'PENDING_AP' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleAPCompletion}
            >
              Mark Complete (AP)
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={handleRejection}
            >
              Reject
            </Button>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ChevronLeft />}
              size="large"
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              {(formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED') && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={handleSaveForm}
                >
                  Save Draft
                </Button>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                {nextStepName && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Next: {nextStepName}
                  </Typography>
                )}
                {activeStep === steps.length - 1 && (formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED') ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmitForApproval}
                  >
                    Submit for Approval
                  </Button>
                ) : activeStep < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ChevronRight />}
                    size="large"
                  >
                    Next
                  </Button>
                ) : null}
              </Box>
            </Box>
        </Box>
      </Paper>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this vendor due diligence request:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionNotes}
            onChange={(e) => setRejectionNotes(e.target.value)}
            placeholder="Explain why this request is being rejected..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Yes, Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MultiStepForm;
